import { useEffect, useRef } from "react";
import { ListaSearchCombobox } from "@/components/ListaSearchCombobox";
import { CentroCustoSearchCombobox } from "@/components/CentroCustoSearchCombobox";
import { rotuloConta, usePlanoContasLookup } from "@/components/ContaContabilSearchCombobox";
import { useForm, Controller, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { contaPagarSchema, contaPagarEditSchema, preverVencimentos, type ContaPagarFormData } from "../schemas/contaPagarSchema";
import { useCentrosCusto, useGruposDespesas, useTiposDespesas } from "../hooks/useCadastrosFinanceiros";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PessoaSearchCombobox } from "@/components/PessoaSearchCombobox";

interface ContaPagarFormProps {
  onSubmit: (data: ContaPagarFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  defaultValues?: Partial<ContaPagarFormData>;
  /** Rótulo do botão de confirmação (edição usa "Salvar alterações"). */
  submitLabel?: string;
  /**
   * Grupo/tipo/centro de despesa são obrigatórios? Padrão true (lançamento). Na
   * EDIÇÃO passa false: títulos migrados vêm sem classificação e não se deve
   * obrigar a classificar só para corrigir valor/vencimento.
   */
  exigirClassificacao?: boolean;
  /**
   * Trava os campos de PARCELAMENTO (valor, quantidade, vencimento, dias, dia
   * fixo). Usado na edição de um título com pagamento: mudar o parcelamento
   * regeraria o carnê e apagaria o histórico de baixa (o backend recusa). A
   * classificação e os metadados continuam editáveis.
   */
  bloquearParcelamento?: boolean;
}

const brl = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export const ContaPagarForm = ({ onSubmit, onCancel, isLoading, defaultValues, submitLabel, exigirClassificacao = true, bloquearParcelamento = false }: ContaPagarFormProps) => {
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ContaPagarFormData>({
    resolver: zodResolver(exigirClassificacao ? contaPagarSchema : contaPagarEditSchema),
    defaultValues: { qtdParcelas: 1, diaFixo: false, ...defaultValues },
  });
  const obg = exigirClassificacao ? " *" : "";

  const grupoDespesasId = watch("grupoDespesasId");
  const tipoDespesasId = watch("tipoDespesasId");
  const centroCustoId = watch("centroCustoId");
  const contaContabilId = watch("contaContabilId");
  const { data: plano = [] } = usePlanoContasLookup();

  // Pré-visualização do carnê enquanto o usuário digita — o servidor recalcula
  // com a mesma regra ao gravar; isto é só para conferência.
  const [dtVencimento, qtdParcelas, valorParcela, diasParcelas, diaFixo] = useWatch({
    control,
    name: ["dtVencimento", "qtdParcelas", "valorParcela", "diasParcelas", "diaFixo"],
  });
  const qtd = Number(qtdParcelas) || 1;
  const valor = Number(valorParcela) || 0;
  const vencimentos = preverVencimentos(dtVencimento ?? "", qtd, diasParcelas, !!diaFixo);
  // Trava dos campos de parcelamento na edição de título já pago.
  const travado = bloquearParcelamento || isLoading;
  const totalCarne = valor * qtd;

  const { gruposDespesas, isLoading: loadingGrupos } = useGruposDespesas();
  const { tiposDespesas, isLoading: loadingTipos } = useTiposDespesas(grupoDespesasId);
  const { centrosCusto, isLoading: loadingCentros } = useCentrosCusto();

  // As três cascatas abaixo só podem reagir a uma mudança REAL feita pelo
  // usuário, não à hidratação inicial nem ao recarregamento assíncrono das
  // listas (tiposDespesas chega depois). Cada uma guarda o último valor que
  // tratou: se o valor observado é igual, é hidratação/re-render e não faz nada.
  // Sem isso, ao ABRIR a edição a lista pré-preenchida (grupo→tipo→centro) era
  // zerada no mount — o motivo de "editar não fazer nada" de forma útil.
  const grupoAnterior  = useRef<number | undefined>(defaultValues?.grupoDespesasId);
  const tipoAnterior   = useRef<number | undefined>(defaultValues?.tipoDespesasId);
  const centroAnterior = useRef<number | undefined>(defaultValues?.centroCustoId);

  // Usuário trocou o grupo → o tipo escolhido não vale mais.
  useEffect(() => {
    if (grupoDespesasId === grupoAnterior.current) return;
    grupoAnterior.current = grupoDespesasId;
    setValue("tipoDespesasId", 0 as unknown as number);
  }, [grupoDespesasId, setValue]);

  // Usuário trocou o tipo → sugerir o centro de custo dele.
  useEffect(() => {
    if (tipoDespesasId === tipoAnterior.current) return;
    tipoAnterior.current = tipoDespesasId;
    const tipo = tiposDespesas.find((t) => t.id === tipoDespesasId);
    if (tipo?.centroCustoId) {
      setValue("centroCustoId", tipo.centroCustoId);
    }
  }, [tipoDespesasId, tiposDespesas, setValue]);

  // Centro mudou → derivar a conta contábil (campo oculto).
  useEffect(() => {
    if (centroCustoId === centroAnterior.current) return;
    centroAnterior.current = centroCustoId;
    const centro = centrosCusto.find((c) => c.id === centroCustoId);
    if (centro?.contaContabilId) {
      setValue("contaContabilId", centro.contaContabilId);
    }
  }, [centroCustoId, centrosCusto, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4 py-4">
      {/* Fornecedor */}
      <div className="grid gap-2">
        <Label>Fornecedor *</Label>
        <Controller
          name="fornecedorId"
          control={control}
          render={({ field }) => (
            <PessoaSearchCombobox
              value={field.value}
              onChange={field.onChange}
              filter="fornecedor"
              placeholder="Buscar fornecedor..."
              disabled={isLoading}
            />
          )}
        />
        {errors.fornecedorId && (
          <p className="text-sm text-destructive">{errors.fornecedorId.message}</p>
        )}
      </div>

      {/* Grupo de Despesas */}
      <div className="grid gap-2">
        <Label>Grupo de Despesas{obg}</Label>
        <Controller
          name="grupoDespesasId"
          control={control}
          render={({ field }) => (
            <ListaSearchCombobox
              chave="grupo-despesas"
              opcoes={gruposDespesas}
              value={field.value || null}
              onChange={(id) => field.onChange(id ?? 0)}
              disabled={isLoading || loadingGrupos}
              permitirLimpar={false}
              placeholder="Buscar grupo de despesas..."
            />
          )}
        />
        {errors.grupoDespesasId && (
          <p className="text-sm text-destructive">{errors.grupoDespesasId.message}</p>
        )}
      </div>

      {/* Tipo de Despesas */}
      <div className="grid gap-2">
        <Label>Tipo de Despesas{obg}</Label>
        <Controller
          name="tipoDespesasId"
          control={control}
          render={({ field }) => (
            <ListaSearchCombobox
              chave={`tipo-despesas-${grupoDespesasId ?? 0}`}
              opcoes={tiposDespesas}
              value={field.value || null}
              onChange={(id) => field.onChange(id ?? 0)}
              disabled={isLoading || loadingTipos || !grupoDespesasId}
              permitirLimpar={false}
              placeholder="Buscar tipo de despesas..."
            />
          )}
        />
        {errors.tipoDespesasId && (
          <p className="text-sm text-destructive">{errors.tipoDespesasId.message}</p>
        )}
      </div>

      {/* Centro de Custo */}
      <div className="grid gap-2">
        <Label>Centro de Custo{obg}</Label>
        <Controller
          name="centroCustoId"
          control={control}
          render={({ field }) => (
            <CentroCustoSearchCombobox
              value={field.value || null}
              onChange={(id) => field.onChange(id ?? 0)}
              disabled={isLoading || loadingCentros}
              permitirLimpar={false}
              placeholder="Buscar centro de custo..."
            />
          )}
        />
        {errors.centroCustoId && (
          <p className="text-sm text-destructive">{errors.centroCustoId.message}</p>
        )}
      </div>

      {/* Campo oculto — contaContabilId derivado do centro de custo.
          O erro dele PRECISA aparecer: sendo um input hidden, uma conta contábil
          ausente (centro de custo com id_conta_contabil = 0) reprovava o schema
          sem nada na tela, e o formulário simplesmente não submetia — beco sem
          saída em que o usuário conclui que "não dá para lançar". */}
      <input type="hidden" {...register("contaContabilId")} />
      {!!contaContabilId && (
        <p className="text-xs text-muted-foreground -mt-2">
          Conta contábil: <span className="font-medium text-foreground">
            {rotuloConta(plano.find((c) => c.id === contaContabilId)) || `#${contaContabilId}`}
          </span> (vem do centro de custo)
        </p>
      )}
      {errors.contaContabilId && (
        <p className="text-sm text-destructive">
          {errors.contaContabilId.message} — o centro de custo selecionado não tem
          conta contábil vinculada. Ajuste o cadastro do centro de custo.
        </p>
      )}

      {/* Datas */}
      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label htmlFor="dtEmissao">Data de Emissão *</Label>
          <Input
            id="dtEmissao"
            type="date"
            {...register("dtEmissao")}
            disabled={isLoading}
          />
          {errors.dtEmissao && (
            <p className="text-sm text-destructive">{errors.dtEmissao.message}</p>
          )}
        </div>
        <div className="grid gap-2">
          <Label htmlFor="dtVencimento">Data de Vencimento *</Label>
          <Input
            id="dtVencimento"
            type="date"
            {...register("dtVencimento")}
            disabled={travado}
          />
          {errors.dtVencimento && (
            <p className="text-sm text-destructive">{errors.dtVencimento.message}</p>
          )}
        </div>
      </div>

      {bloquearParcelamento && (
        <p className="text-xs text-muted-foreground -mt-1">
          Este título já tem pagamento: valor, parcelas e vencimento ficam
          bloqueados. Você pode corrigir a classificação e os dados do documento.
        </p>
      )}

      {/* Valor da parcela + documento */}
      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label htmlFor="valorParcela">Valor da Parcela (R$) *</Label>
          <Input
            id="valorParcela"
            type="number"
            step="0.01"
            placeholder="0,00"
            {...register("valorParcela")}
            disabled={travado}
          />
          <p className="text-xs text-muted-foreground">
            Valor de UMA parcela — o título é este valor × a quantidade.
          </p>
          {errors.valorParcela && (
            <p className="text-sm text-destructive">{errors.valorParcela.message}</p>
          )}
        </div>
        <div className="grid gap-2">
          <Label htmlFor="numDocumento">Nº Documento</Label>
          <Input id="numDocumento" placeholder="000000" {...register("numDocumento")} disabled={isLoading} />
          {errors.numDocumento && (
            <p className="text-sm text-destructive">{errors.numDocumento.message}</p>
          )}
        </div>
      </div>

      {/* Parcelamento */}
      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label htmlFor="qtdParcelas">Qtd. Parcelas</Label>
          <Input
            id="qtdParcelas"
            type="number"
            min={1}
            max={360}
            {...register("qtdParcelas")}
            disabled={travado}
          />
          {errors.qtdParcelas && (
            <p className="text-sm text-destructive">{errors.qtdParcelas.message}</p>
          )}
        </div>
        <div className="grid gap-2">
          <Label htmlFor="diasParcelas">Dias entre parcelas</Label>
          <Input
            id="diasParcelas"
            placeholder="30 ou 7,14,28"
            {...register("diasParcelas")}
            disabled={travado}
          />
          <p className="text-xs text-muted-foreground">
            Dias a partir do 1º vencimento. Vazio = 30 em 30.
          </p>
          {errors.diasParcelas && (
            <p className="text-sm text-destructive">{errors.diasParcelas.message}</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Controller
          name="diaFixo"
          control={control}
          render={({ field }) => (
            <Checkbox
              id="diaFixo"
              checked={!!field.value}
              onCheckedChange={field.onChange}
              disabled={travado}
            />
          )}
        />
        <Label htmlFor="diaFixo" className="font-normal cursor-pointer">
          Dia fixo (manter o dia do mês do 1º vencimento)
        </Label>
      </div>

      {/* Descrição */}
      <div className="grid gap-2">
        <Label htmlFor="lancamento">Descrição</Label>
        <Input
          id="lancamento"
          placeholder="Ex.: Aluguel, energia, contrato mensal…"
          {...register("lancamento")}
          disabled={isLoading}
        />
        {errors.lancamento && (
          <p className="text-sm text-destructive">{errors.lancamento.message}</p>
        )}
      </div>

      {/* Prévia do carnê: só quando o parcelamento está editável. */}
      {!bloquearParcelamento && vencimentos.length > 0 && valor > 0 && (
        <div className="rounded-md border p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">
              {vencimentos.length} parcela(s) de {brl(valor)}
            </span>
            <span className="text-sm font-semibold">Total: {brl(totalCarne)}</span>
          </div>
          <div className="max-h-40 overflow-y-auto">
            <table className="w-full text-sm">
              <tbody>
                {vencimentos.map((d, i) => (
                  <tr key={i} className="border-t first:border-t-0">
                    <td className="py-1 text-muted-foreground w-16">{i + 1}/{vencimentos.length}</td>
                    <td className="py-1">{d.toLocaleDateString("pt-BR")}</td>
                    <td className="py-1 text-right">{brl(valor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? "Salvando..." : (submitLabel ?? "Salvar")}
        </Button>
      </div>
    </form>
  );
};
